import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import {
  useTiktokAgentConfigs,
  useTiktokSaveAgentConfig,
  useTiktokDeleteAgentConfig,
} from '@/api/useTiktok';
import { useTiktokStore } from '@/stores/useTiktokStore';
import type { TiktokAgentConfig } from '@/types/outreach';
import styles from './TiktokAgentConfigPanel.module.css';

const EMPTY_CONFIG: Partial<TiktokAgentConfig> = {
  name: '',
  systemPrompt: '',
  isActive: true,
  isDefault: false,
  maxResponseLength: 300,
  autoReplyToComments: false,
  commentReplyDelayMs: 3000,
};

export function TiktokAgentConfigPanel() {
  const { showAgentConfig, setShowAgentConfig } = useTiktokStore();
  const { data: configs } = useTiktokAgentConfigs();
  const saveConfig = useTiktokSaveAgentConfig();
  const deleteConfig = useTiktokDeleteAgentConfig();

  const [selected, setSelected] = useState<Partial<TiktokAgentConfig>>(EMPTY_CONFIG);
  const [isNew, setIsNew] = useState(true);

  useEffect(() => {
    if (configs && configs.length > 0 && isNew && !selected.id) {
      setSelected(configs[0]);
      setIsNew(false);
    }
  }, [configs]);

  if (!showAgentConfig) return null;

  const handleSelect = (config: TiktokAgentConfig) => {
    setSelected(config);
    setIsNew(false);
  };

  const handleNew = () => {
    setSelected({ ...EMPTY_CONFIG });
    setIsNew(true);
  };

  const handleSave = () => {
    if (!selected.name || !selected.systemPrompt) return;
    saveConfig.mutate(selected as TiktokAgentConfig & { name: string; systemPrompt: string }, {
      onSuccess: (saved) => {
        setSelected(saved);
        setIsNew(false);
      },
    });
  };

  const handleDelete = () => {
    if (!selected.id) return;
    deleteConfig.mutate(selected.id, {
      onSuccess: () => {
        setSelected({ ...EMPTY_CONFIG });
        setIsNew(true);
      },
    });
  };

  return (
    <div className={styles.overlay} onClick={() => setShowAgentConfig(false)}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>TikTok Agent Configuration</h3>
          <button onClick={() => setShowAgentConfig(false)} className={styles.closeBtn}>
            <X className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.body}>
          {/* Config selector */}
          <div className={styles.configSelector}>
            <div className={styles.configList}>
              {(configs ?? []).map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSelect(c)}
                  className={`${styles.configItem} ${selected.id === c.id ? styles.configItemActive : ''}`}
                >
                  {c.name}
                  {c.isDefault && <span className={styles.defaultBadge}>Default</span>}
                </button>
              ))}
            </div>
            <button onClick={handleNew} className={styles.newBtn}>
              <Plus className={styles.newIcon} /> New
            </button>
          </div>

          {/* Form */}
          <div className={styles.form}>
            <label className={styles.label}>
              Name
              <input
                type="text"
                value={selected.name ?? ''}
                onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                className={styles.input}
              />
            </label>

            <label className={styles.label}>
              System Prompt
              <textarea
                value={selected.systemPrompt ?? ''}
                onChange={(e) => setSelected({ ...selected, systemPrompt: e.target.value })}
                className={styles.textarea}
                rows={6}
              />
            </label>

            <div className={styles.row}>
              <label className={styles.label}>
                Max Response Length
                <input
                  type="number"
                  value={selected.maxResponseLength ?? 300}
                  onChange={(e) =>
                    setSelected({ ...selected, maxResponseLength: Number(e.target.value) })
                  }
                  className={styles.input}
                />
              </label>
              <label className={styles.label}>
                Reply Delay (ms)
                <input
                  type="number"
                  value={selected.commentReplyDelayMs ?? 3000}
                  onChange={(e) =>
                    setSelected({ ...selected, commentReplyDelayMs: Number(e.target.value) })
                  }
                  className={styles.input}
                />
              </label>
            </div>

            <div className={styles.checkboxRow}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={selected.isActive ?? true}
                  onChange={(e) => setSelected({ ...selected, isActive: e.target.checked })}
                />
                Active
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={selected.isDefault ?? false}
                  onChange={(e) => setSelected({ ...selected, isDefault: e.target.checked })}
                />
                Default
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={selected.autoReplyToComments ?? false}
                  onChange={(e) =>
                    setSelected({ ...selected, autoReplyToComments: e.target.checked })
                  }
                />
                Auto-Reply to Comments
              </label>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          {!isNew && selected.id && (
            <button
              onClick={handleDelete}
              className={styles.deleteBtn}
              disabled={deleteConfig.isPending}
            >
              <Trash2 className={styles.footerIcon} /> Delete
            </button>
          )}
          <button onClick={handleSave} className={styles.saveBtn} disabled={saveConfig.isPending}>
            <Save className={styles.footerIcon} /> {isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
